import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { useMemo } from "react";

import { GetActivityQuery, GetActivityQueryVariables } from "@/graphql/graphql";

import { GET_ACTIVITY_QUERY } from "./ActivityDialog.queries";
import {
  activityDialogAuthorSx,
  activityDialogContentSx,
  activityDialogDescriptionBoxSx,
  activityDialogDescriptionTextSx,
  activityDialogTitleRowSx,
  activityDialogTitleTextSx,
} from "./ActivityDialog.styles";
import QuestionBody from "./QuestionBody";

const ActivityDialog: React.FC<{
  open: boolean;
  activityId: number | null;
  onClose: () => void;
  /** Title from the list row while the full activity is loading */
  titleHint?: string | null;
}> = ({ open, activityId, onClose, titleHint }) => {
  const { data, loading, error } = useQuery<GetActivityQuery, GetActivityQueryVariables>(
    GET_ACTIVITY_QUERY,
    {
      variables: { id: activityId ?? 0 },
      skip: !open || activityId == null,
    }
  );

  const sortedContents = useMemo(() => {
    const rows = data?.activity.contents;
    if (!rows?.length) {
      return [];
    }
    return [...rows].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
  }, [data?.activity.contents]);

  const title = data?.activity.title ?? titleHint ?? "Activity";
  const description = data?.activity.description;
  const fetchFailed = Boolean(error);
  const showSpinner = loading && !data?.activity;
  const showDescription = Boolean(description?.trim()) && !fetchFailed && !showSpinner;

  const createdBy = data?.activity.createdBy;
  const authorLine =
    createdBy &&
    [createdBy.firstName, createdBy.lastName]
      .filter((part) => part?.trim())
      .join(" ")
      .trim();

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={activityDialogTitleRowSx}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="span" sx={activityDialogTitleTextSx}>
            {title}
          </Typography>
          {authorLine && (
            <Typography
              component="p"
              variant="body2"
              sx={activityDialogAuthorSx}
              aria-label={`Activity author: ${authorLine}`}
            >
              {authorLine}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close activity details" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={activityDialogContentSx}>
        {showDescription && (
          <Box sx={activityDialogDescriptionBoxSx}>
            <Typography component="p" sx={activityDialogDescriptionTextSx}>
              {description}
            </Typography>
          </Box>
        )}
        <QuestionBody error={fetchFailed} showSpinner={showSpinner} steps={sortedContents} />
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDialog;
